import { Bot, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';
import type { AIMessage } from '../types';

interface ChatMessageProps {
  message: AIMessage;
  userName?: string;
}

export function ChatMessage({ message, userName }: ChatMessageProps) {
  const { t, i18n } = useTranslation('aiChat');
  const isUser = message.role === 'user';

  // Format timestamp
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'en' ? 'en-GB' : 'de-DE';
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Parse markdown-like formatting (basic)
  const formatContent = (content: string) => {
    // Bold: **text**
    let formatted = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Newlines to <br>
    formatted = formatted.replace(/\n/g, '<br />');
    return formatted;
  };

  return (
    <div
      className={cn(
        'flex gap-3 mb-4',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
          isUser ? 'bg-primary-100' : 'bg-gray-100'
        )}
      >
        {isUser ? (
          <User className="w-4 h-4 text-primary-600" />
        ) : (
          <Bot className="w-4 h-4 text-gray-600" />
        )}
      </div>

      {/* Message Bubble */}
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-3',
          isUser
            ? 'bg-primary-600 text-white rounded-tr-sm'
            : 'bg-gray-100 text-gray-900 rounded-tl-sm'
        )}
      >
        {/* Sender Name */}
        <p
          className={cn(
            'text-xs font-medium mb-1',
            isUser ? 'text-primary-100' : 'text-gray-500'
          )}
        >
          {isUser ? (userName || t('message.you')) : t('message.assistant')}
        </p>

        {/* Content */}
        <div
          className={cn(
            'text-sm leading-relaxed',
            isUser ? 'text-white' : 'text-gray-800'
          )}
          dangerouslySetInnerHTML={{ __html: formatContent(message.content || '') }}
        />

        {/* Timestamp */}
        <p
          className={cn(
            'text-[10px] mt-2',
            isUser ? 'text-primary-200 text-right' : 'text-gray-400'
          )}
        >
          {formatTime(message.created_at)}
        </p>
      </div>
    </div>
  );
}
